import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Table,
  Space,
  Tooltip,
  Button,
  PageHeader,
  Result,
  Spin,
  Modal,
  Form,
  Empty,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import {
  handleApiSuccess,
  handleApiError,
  confirmDelete,
  accessControls,
  MyBreadcrumb,
} from 'utils';
import {
  schedulingActivitiesListService,
  schedulingActivityCreateService,
  schedulingActivityUpdateService,
  schedulingActivityDeleteService,
} from 'services/scheduling';
import ActivityForm from './form';
import '../scheduling.css';

export default function ActivitiesList() {
  const history = useHistory();
  const [permissions, setPermissions] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    accessControls
      .fetch()
      .then(setPermissions)
      .catch(() => setPermissions({}));
  }, []);

  const loadData = () => {
    setIsLoading(true);
    schedulingActivitiesListService()
      .then((res) => {
        setIsLoading(false);
        setDataSource(res.data.items || []);
      })
      .catch((err) => {
        setIsLoading(false);
        handleApiError(err);
      });
  };

  useEffect(() => {
    if (permissions == null) return;
    if (!accessControls.can(permissions, 'Scheduling.read:any')) return;
    loadData();
  }, [permissions]);

  const openModal = (record) => {
    setEditing(record || null);
    form.setFieldsValue(
      record
        ? { name: record.name, color: record.color || '#1890ff' }
        : { name: '', color: '#1890ff' },
    );
    setModalVisible(true);
  };

  const saveRecord = () => {
    form.validateFields().then((values) => {
      setSaving(true);
      const req = editing
        ? schedulingActivityUpdateService(editing._id, values)
        : schedulingActivityCreateService(values);
      req
        .then((res) => {
          setSaving(false);
          handleApiSuccess(res);
          setModalVisible(false);
          loadData();
        })
        .catch((err) => {
          setSaving(false);
          handleApiError(err);
        });
    });
  };

  const deleteRecord = (id) => {
    confirmDelete().then(() => {
      setIsLoading(true);
      schedulingActivityDeleteService(id)
        .then((res) => {
          setIsLoading(false);
          handleApiSuccess(res);
          loadData();
        })
        .catch((err) => {
          setIsLoading(false);
          handleApiError(err);
        });
    });
  };

  const canUpdate = accessControls.can(permissions, 'Scheduling.update:any');

  const columns = [
    {
      title: 'Activity',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) =>
        (a.name || '').localeCompare(b.name || '', undefined, {
          sensitivity: 'base',
        }),
      render: (text, record) => (
        <Space>
          <span
            className="scheduling-color-swatch"
            style={{ background: record.color || '#d9d9d9' }}
          />
          <span style={{ fontWeight: 500 }}>{text}</span>
        </Space>
      ),
    },
    {
      title: 'Color',
      dataIndex: 'color',
      key: 'color',
      width: 140,
      render: (color) => color || 'Default',
    },
    ...(canUpdate
      ? [
          {
            title: 'Action',
            key: 'action',
            width: 120,
            render: (_, record) => (
              <Space size="small">
                <Tooltip title="Edit">
                  <Button
                    type="primary"
                    shape="circle"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => openModal(record)}
                  />
                </Tooltip>
                <Tooltip title="Delete">
                  <Button
                    shape="circle"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => deleteRecord(record._id)}
                  />
                </Tooltip>
              </Space>
            ),
          },
        ]
      : []),
  ];

  if (permissions == null) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!accessControls.can(permissions, 'Scheduling.read:any')) {
    return (
      <div style={{ padding: 24 }}>
        <Result
          status="403"
          title="Access denied"
          subTitle="You do not have permission to view schedule activities."
          extra={
            <Button type="primary" onClick={() => history.push('/')}>
              Go to Dashboard
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div>
      <MyBreadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Time Tracker', path: '/time-tracker/tracker' },
          { label: 'Schedules', path: '/time-tracker/scheduling' },
          { label: 'Activities', path: '' },
        ]}
      />
      <PageHeader
        ghost={false}
        onBack={() => history.push('/time-tracker/scheduling')}
        title="Activities"
        subTitle="Categories for shifts (Phone Answering, Admin, LMS, etc.)"
        extra={
          canUpdate
            ? [
                <Button
                  key="create"
                  type="primary"
                  icon={<PlusCircleOutlined />}
                  onClick={() => openModal()}
                >
                  New activity
                </Button>,
              ]
            : []
        }
      />
      <Table
        rowKey="_id"
        loading={isLoading}
        dataSource={dataSource}
        columns={columns}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        size="middle"
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No activities yet"
            >
              {canUpdate && (
                <Button
                  type="primary"
                  icon={<PlusCircleOutlined />}
                  onClick={() => openModal()}
                >
                  Create an activity
                </Button>
              )}
            </Empty>
          ),
        }}
      />
      <Modal
        title={editing ? 'Edit activity' : 'New activity'}
        visible={modalVisible}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={saveRecord}
        confirmLoading={saving}
        destroyOnClose
        okText={editing ? 'Update' : 'Create'}
        width={480}
      >
        <ActivityForm form={form} initialValues={{ color: '#1890ff' }} />
      </Modal>
    </div>
  );
}
